import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { X } from 'lucide-react'
import type { PricingItem } from '@/types'

/**
 * A striping surcharge (New Layout / Double Coat added cost) is "auto" — derived from
 * restripe cost — unless an explicit value was already saved, including a deliberate
 * $0 override. Must be a nullish check: a falsy check would treat a saved $0 as "auto"
 * and let it be silently overwritten the next time restripe cost changes.
 */
export function isSurchargeAuto(savedCost: number | undefined | null): boolean {
  return savedCost == null
}

interface AddItemDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: {
    name: string
    rate: number
    description?: string
    category: string
    service?: string
    overtimeRate?: number
    publicWorksRate?: number
    unit?: string
    fuelCharge?: number
    variableCost?: boolean
    restripeCost?: number
    stripingCategory?: string
    newLayoutEnabled?: boolean
    newLayoutAdditionalCost?: number
    doubleCoatEnabled?: boolean
    doubleCoatAdditionalCost?: number
    serviceRates?: Record<string, { rate: number; overtimeRate: number; publicWorksRate: number }>
  }) => void
  editItem?: PricingItem
  category: string
  error?: string | null
  defaultFuelChargePercent?: number
}

const LABOR_SERVICE_TABS = ['Asphalt', 'Concrete', 'Grading', 'Seal Coating', 'Striping']
const UNIT_TYPE_OPTIONS = ['Each', 'Per Foot', 'Per Hour', 'Per Day', 'Per Days']
// NOTE: the estimate builder (striping-estimate-panel.tsx STRIPING_SECTIONS) renders
// only these six sub-categories. A plain 'Striping' option used to live here too, but
// nothing consumes it — no estimate section, no seed data, no logic — so an item filed
// under it could never appear on an estimate. Removed to stop the empty/orphaned tab.
// (If a tenant already has items saved as 'Striping', the pricing page still surfaces
// that tab via its data-derived categories, so no data is hidden.)
export const STRIPING_CATEGORY_OPTIONS = ['Stencils', 'ADA Stencils', 'Stalls', 'School Striping', 'Concrete Wheel Stops', 'Other Striping']
const MATERIALS_UNIT_OPTIONS = [
  'Ton',
  'Cubic Yard',
  'Square Foot',
  'Square Yard',
  'Linear Foot',
  'Foot',
  'Gallon',
  'Each',
  'Count',
  'Bag',
  'Load',
  'Roll',
]

const emptyServiceRate = () => ({ rate: 0, overtimeRate: 0, publicWorksRate: 0 })

export function AddItemDialog({
  open,
  onClose,
  onSubmit,
  editItem,
  category,
  error,
  defaultFuelChargePercent = 18,
}: AddItemDialogProps) {
  const fuelChargeDecimal = defaultFuelChargePercent / 100
  const defaults = {
    name: '',
    rate: 0,
    service: 'Asphalt',
    services: [] as string[],
    overtimeRate: 0,
    publicWorksRate: 0,
    unit: category === 'materials' ? 'Ton' : category === 'striping' ? 'each' : 'Each',
    fuelCharge: fuelChargeDecimal,
    variableCost: false,
    restripeCost: 0,
    stripingCategory: 'Stencils',
    newLayoutEnabled: false,
    newLayoutAdditionalCost: 0,
    doubleCoatEnabled: false,
    doubleCoatAdditionalCost: 0,
  }

  const [formData, setFormData] = useState({ ...defaults })

  // Labor: per-service rates state
  const [laborRates, setLaborRates] = useState<Record<string, { rate: number; overtimeRate: number; publicWorksRate: number }>>({})
  const [activeServiceTab, setActiveServiceTab] = useState(LABOR_SERVICE_TABS[0])

  // Equipment: per-service rates state
  const [equipmentRates, setEquipmentRates] = useState<Record<string, { rate: number }>>({})
  const [activeEquipmentTab, setActiveEquipmentTab] = useState(LABOR_SERVICE_TABS[0])

  // Misc: per-service rates state
  const [miscRates, setMiscRates] = useState<Record<string, { rate: number }>>({})
  const [activeMiscTab, setActiveMiscTab] = useState(LABOR_SERVICE_TABS[0])

  // Materials: per-service rates state
  const [materialRates, setMaterialRates] = useState<Record<string, { rate: number }>>({})
  const [activeMaterialTab, setActiveMaterialTab] = useState(LABOR_SERVICE_TABS[0])

  // ── Striping New Layout / Double Coat auto-pricing ──
  // Enabling an option adds a standard percentage of the restripe cost. The added
  // amount is auto-derived (and kept in sync as the restripe cost changes) unless the
  // estimator overrides it. Percentages match NPG's existing striping items.
  const NEW_LAYOUT_PCT = 0.40
  const DOUBLE_COAT_PCT = 0.60
  const round2 = (n: number) => Math.round(n * 100) / 100
  const [newLayoutAuto, setNewLayoutAuto] = useState(true)
  const [doubleCoatAuto, setDoubleCoatAuto] = useState(true)

  // On open, an option's added cost is auto unless we're editing an item that already
  // has a saved cost — in which case preserve it as a manual override.
  useEffect(() => {
    if (!open) return
    setNewLayoutAuto(isSurchargeAuto(editItem?.newLayoutAdditionalCost))
    setDoubleCoatAuto(isSurchargeAuto(editItem?.doubleCoatAdditionalCost))
  }, [open, editItem])

  const applyRestripeCost = (v: number) => {
    setFormData((fd) => ({
      ...fd,
      restripeCost: v,
      newLayoutAdditionalCost: fd.newLayoutEnabled && newLayoutAuto ? round2(v * NEW_LAYOUT_PCT) : fd.newLayoutAdditionalCost,
      doubleCoatAdditionalCost: fd.doubleCoatEnabled && doubleCoatAuto ? round2(v * DOUBLE_COAT_PCT) : fd.doubleCoatAdditionalCost,
    }))
  }
  const toggleNewLayout = (checked: boolean) => {
    setFormData((fd) => ({
      ...fd,
      newLayoutEnabled: checked,
      newLayoutAdditionalCost: checked && newLayoutAuto ? round2((fd.restripeCost || 0) * NEW_LAYOUT_PCT) : fd.newLayoutAdditionalCost,
    }))
    if (!checked) setNewLayoutAuto(true) // re-enabling recomputes fresh
  }
  const toggleDoubleCoat = (checked: boolean) => {
    setFormData((fd) => ({
      ...fd,
      doubleCoatEnabled: checked,
      doubleCoatAdditionalCost: checked && doubleCoatAuto ? round2((fd.restripeCost || 0) * DOUBLE_COAT_PCT) : fd.doubleCoatAdditionalCost,
    }))
    if (!checked) setDoubleCoatAuto(true)
  }
  const setNewLayoutCost = (v: number) => { setNewLayoutAuto(false); setFormData((fd) => ({ ...fd, newLayoutAdditionalCost: v })) }
  const setDoubleCoatCost = (v: number) => { setDoubleCoatAuto(false); setFormData((fd) => ({ ...fd, doubleCoatAdditionalCost: v })) }

  // Parse comma-separated service string into array for multi-select
  const parseServices = (svc?: string): string[] =>
    svc ? svc.split(',').map(s => s.trim()).filter(Boolean) : []

  useEffect(() => {
    if (editItem) {
      setFormData({
        name: editItem.name,
        rate: editItem.rate,
        service: editItem.service || 'Asphalt',
        services: ['equipment', 'miscellaneous', 'misc'].includes(category) ? parseServices(editItem.service) : [],
        overtimeRate: editItem.overtimeRate || 0,
        publicWorksRate: editItem.publicWorksRate || 0,
        unit: editItem.unit || 'Each',
        fuelCharge: editItem.fuelCharge ?? fuelChargeDecimal,
        variableCost: editItem.variableCost ?? false,
        restripeCost: editItem.restripeCost ?? 0,
        stripingCategory: editItem.stripingCategory || 'Stencils',
        newLayoutEnabled: editItem.newLayoutEnabled ?? false,
        newLayoutAdditionalCost: editItem.newLayoutAdditionalCost ?? 0,
        doubleCoatEnabled: editItem.doubleCoatEnabled ?? false,
        doubleCoatAdditionalCost: editItem.doubleCoatAdditionalCost ?? 0,
      })
      // Populate labor rates from existing serviceRates or legacy fields
      if (category === 'labor') {
        if (editItem.serviceRates && typeof editItem.serviceRates === 'object') {
          setLaborRates({ ...editItem.serviceRates })
        } else {
          // Legacy: build from flat fields
          const svcs = parseServices(editItem.service)
          const rates: Record<string, { rate: number; overtimeRate: number; publicWorksRate: number }> = {}
          for (const svc of svcs) {
            rates[svc] = {
              rate: editItem.rate || 0,
              overtimeRate: editItem.overtimeRate || 0,
              publicWorksRate: editItem.publicWorksRate || 0,
            }
          }
          setLaborRates(rates)
        }
        // Set active tab to first populated service, or first tab
        const populated = LABOR_SERVICE_TABS.find(t => {
          const sr = editItem.serviceRates as Record<string, { rate: number }> | undefined
          return sr?.[t] && sr[t].rate > 0
        })
        setActiveServiceTab(populated || LABOR_SERVICE_TABS[0])
      }
      // Populate equipment rates
      if (category === 'equipment') {
        if (editItem.serviceRates && typeof editItem.serviceRates === 'object') {
          const eqRates: Record<string, { rate: number }> = {}
          for (const [svc, r] of Object.entries(editItem.serviceRates)) {
            eqRates[svc] = { rate: (r as { rate: number }).rate || 0 }
          }
          setEquipmentRates(eqRates)
        } else {
          const svcs = parseServices(editItem.service)
          const rates: Record<string, { rate: number }> = {}
          for (const svc of svcs) { rates[svc] = { rate: editItem.rate || 0 } }
          setEquipmentRates(rates)
        }
        const pop = LABOR_SERVICE_TABS.find(t => {
          const sr = editItem.serviceRates as Record<string, { rate: number }> | undefined
          return sr?.[t] && sr[t].rate > 0
        })
        setActiveEquipmentTab(pop || LABOR_SERVICE_TABS[0])
      }
      // Populate misc rates
      if (category === 'miscellaneous' || category === 'misc') {
        if (editItem.serviceRates && typeof editItem.serviceRates === 'object') {
          const mRates: Record<string, { rate: number }> = {}
          for (const [svc, r] of Object.entries(editItem.serviceRates)) {
            mRates[svc] = { rate: (r as { rate: number }).rate || 0 }
          }
          setMiscRates(mRates)
        } else {
          const svcs = parseServices(editItem.service)
          const rates: Record<string, { rate: number }> = {}
          for (const svc of svcs) { rates[svc] = { rate: editItem.rate || 0 } }
          setMiscRates(rates)
        }
        const pop = LABOR_SERVICE_TABS.find(t => {
          const sr = editItem.serviceRates as Record<string, { rate: number }> | undefined
          return sr?.[t] && sr[t].rate > 0
        })
        setActiveMiscTab(pop || LABOR_SERVICE_TABS[0])
      }
      // Populate material rates
      if (category === 'materials') {
        if (editItem.serviceRates && typeof editItem.serviceRates === 'object') {
          const matRates: Record<string, { rate: number }> = {}
          for (const [svc, r] of Object.entries(editItem.serviceRates)) {
            matRates[svc] = { rate: (r as { rate: number }).rate || 0 }
          }
          setMaterialRates(matRates)
        } else {
          // Legacy: build per-service rates from flat rate field
          const svcs = parseServices(editItem.service)
          const rates: Record<string, { rate: number }> = {}
          for (const svc of svcs) { rates[svc] = { rate: editItem.rate || 0 } }
          setMaterialRates(rates)
        }
        const pop = LABOR_SERVICE_TABS.find(t => {
          const sr = editItem.serviceRates as Record<string, { rate: number }> | undefined
          return sr?.[t] && sr[t].rate > 0
        })
        setActiveMaterialTab(pop || LABOR_SERVICE_TABS[0])
      }
    } else {
      setFormData({ ...defaults })
      setLaborRates({})
      setActiveServiceTab(LABOR_SERVICE_TABS[0])
      setEquipmentRates({})
      setActiveEquipmentTab(LABOR_SERVICE_TABS[0])
      setMiscRates({})
      setActiveMiscTab(LABOR_SERVICE_TABS[0])
      setMaterialRates({})
      setActiveMaterialTab(LABOR_SERVICE_TABS[0])
    }
  }, [editItem, open])

  // const _toggleService = (svc: string) => {
  //   setFormData(prev => {
  //     const has = prev.services.includes(svc)
  //     const next = has ? prev.services.filter(s => s !== svc) : [...prev.services, svc]
  //     return { ...prev, services: next, service: next.join(',') }
  //   })
  // }

  // Labor: update a rate field for a service
  const updateLaborRate = (svc: string, field: 'rate' | 'overtimeRate' | 'publicWorksRate', val: number) => {
    setLaborRates(prev => ({
      ...prev,
      [svc]: { ...(prev[svc] || emptyServiceRate()), [field]: val },
    }))
  }

  // Check if a service tab has any non-zero rates (labor)
  const hasRates = (svc: string): boolean => {
    const r = laborRates[svc]
    if (!r) return false
    return r.rate > 0 || r.overtimeRate > 0 || r.publicWorksRate > 0
  }

  // Equipment: update rate for a service
  const updateEquipmentRate = (svc: string, val: number) => {
    setEquipmentRates(prev => ({ ...prev, [svc]: { rate: val } }))
  }
  const hasEquipmentRates = (svc: string): boolean => (equipmentRates[svc]?.rate || 0) > 0

  // Misc: update rate for a service
  const updateMiscRate = (svc: string, val: number) => {
    setMiscRates(prev => ({ ...prev, [svc]: { rate: val } }))
  }
  const hasMiscRates = (svc: string): boolean => (miscRates[svc]?.rate || 0) > 0

  // Materials: update rate for a service
  const updateMaterialRate = (svc: string, val: number) => {
    setMaterialRates(prev => ({ ...prev, [svc]: { rate: val } }))
  }
  const hasMaterialRates = (svc: string): boolean => (materialRates[svc]?.rate || 0) > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (category === 'labor') {
      // Build serviceRates from tabs — only include services with at least one non-zero rate
      const serviceRates: Record<string, { rate: number; overtimeRate: number; publicWorksRate: number }> = {}
      const activeServices: string[] = []
      for (const svc of LABOR_SERVICE_TABS) {
        if (hasRates(svc)) {
          serviceRates[svc] = laborRates[svc]
          activeServices.push(svc)
        }
      }
      onSubmit({
        name: formData.name,
        rate: 0,
        category: 'labor',
        unit: 'hr',
        service: activeServices.join(','),
        serviceRates,
      })
      return
    }

    if (category === 'equipment') {
      const serviceRates: Record<string, { rate: number; overtimeRate: number; publicWorksRate: number }> = {}
      const activeServices: string[] = []
      for (const svc of LABOR_SERVICE_TABS) {
        if (hasEquipmentRates(svc)) {
          serviceRates[svc] = { rate: equipmentRates[svc].rate, overtimeRate: 0, publicWorksRate: 0 }
          activeServices.push(svc)
        }
      }
      onSubmit({
        name: formData.name,
        rate: 0,
        category: 'equipment',
        unit: 'hr',
        fuelCharge: formData.fuelCharge,
        service: activeServices.join(','),
        serviceRates,
      })
      return
    }

    if (category === 'miscellaneous' || category === 'misc') {
      const serviceRates: Record<string, { rate: number; overtimeRate: number; publicWorksRate: number }> = {}
      const activeServices: string[] = []
      for (const svc of LABOR_SERVICE_TABS) {
        if (hasMiscRates(svc)) {
          serviceRates[svc] = { rate: miscRates[svc].rate, overtimeRate: 0, publicWorksRate: 0 }
          activeServices.push(svc)
        }
      }
      onSubmit({
        name: formData.name,
        rate: 0,
        category,
        unit: formData.unit,
        service: activeServices.join(','),
        serviceRates,
      })
      return
    }

    if (category === 'materials') {
      const serviceRates: Record<string, { rate: number; overtimeRate: number; publicWorksRate: number }> = {}
      const activeServices: string[] = []
      for (const svc of LABOR_SERVICE_TABS) {
        if (hasMaterialRates(svc)) {
          serviceRates[svc] = { rate: materialRates[svc].rate, overtimeRate: 0, publicWorksRate: 0 }
          activeServices.push(svc)
        }
      }
      onSubmit({
        name: formData.name,
        rate: 0,
        category: 'materials',
        unit: formData.unit,
        service: activeServices.join(','),
        serviceRates,
      })
      return
    }

    const serviceStr = formData.service
    const submitData = {
      ...formData,
      service: serviceStr,
      category,
    }
    onSubmit(submitData)
  }

  if (!open) return null

  void Card; // used for type, Card wrapper below

  // Count services with rates for the badge
  const activeServiceCount = LABOR_SERVICE_TABS.filter(s => hasRates(s)).length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      />

      {/* Dialog */}
      <Card className={`relative w-full ${['labor', 'equipment', 'miscellaneous', 'misc', 'materials'].includes(category) ? 'max-w-lg' : 'max-w-md'} mx-4 z-50`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">
              {editItem
                ? (category === 'labor' ? 'Edit Laborer' : category === 'equipment' ? 'Edit Equipment' : 'Edit Item')
                : (category === 'labor' ? 'Add Laborer' : category === 'equipment' ? 'Add Equipment' : 'Add Item')}
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name / Worktype */}
            <div>
              <Label htmlFor="name" className="text-sm font-medium">
                {category === 'labor' ? 'Laborer Name' : category === 'striping' ? 'Worktype' : 'Item Name'}
              </Label>
              <Input
                id="name"
                type="text"
                placeholder={category === 'labor' ? 'e.g., Foreman' : category === 'striping' ? 'e.g., 4" White Line' : 'e.g., Asphalt Paver'}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="mt-2"
                required
              />
            </div>

            {/* ═══ Labor: Service tabs with rate fields ═══ */}
            {category === 'labor' && (
              <div>
                <Label className="text-sm font-medium">
                  Services & Rates
                  {activeServiceCount > 0 && (
                    <span className="prc-active-count">{activeServiceCount}</span>
                  )}
                </Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Enter rates for each service. Empty services won't be associated.
                </p>

                {/* Service tabs */}
                <div className="prc-svc-tabs">
                  {LABOR_SERVICE_TABS.map((svc) => (
                    <button
                      key={svc}
                      type="button"
                      className={`prc-svc-tab${activeServiceTab === svc ? ' active' : ''}${hasRates(svc) ? ' has-rates' : ''}`}
                      onClick={() => setActiveServiceTab(svc)}
                    >
                      {svc}
                      {hasRates(svc) && <span className="prc-svc-tab-dot" />}
                    </button>
                  ))}
                </div>

                {/* Rate fields for active tab */}
                <div className="prc-svc-rate-fields">
                  <div>
                    <Label htmlFor={`rate-${activeServiceTab}`} className="text-xs font-medium text-muted-foreground">
                      Per Hour
                    </Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        id={`rate-${activeServiceTab}`}
                        type="number"
                        placeholder="0.00"
                        value={laborRates[activeServiceTab]?.rate || ''}
                        onChange={(e) => updateLaborRate(activeServiceTab, 'rate', parseFloat(e.target.value) || 0)}
                        className="pl-7"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`overtime-${activeServiceTab}`} className="text-xs font-medium text-muted-foreground">
                      Overtime Per Hour
                    </Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        id={`overtime-${activeServiceTab}`}
                        type="number"
                        placeholder="0.00"
                        value={laborRates[activeServiceTab]?.overtimeRate || ''}
                        onChange={(e) => updateLaborRate(activeServiceTab, 'overtimeRate', parseFloat(e.target.value) || 0)}
                        className="pl-7"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`pw-${activeServiceTab}`} className="text-xs font-medium text-muted-foreground">
                      Public Works Per Hour
                    </Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        id={`pw-${activeServiceTab}`}
                        type="number"
                        placeholder="0.00"
                        value={laborRates[activeServiceTab]?.publicWorksRate || ''}
                        onChange={(e) => updateLaborRate(activeServiceTab, 'publicWorksRate', parseFloat(e.target.value) || 0)}
                        className="pl-7"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ Equipment: Service tabs with rate field ═══ */}
            {category === 'equipment' && (() => {
              const activeCount = LABOR_SERVICE_TABS.filter(s => hasEquipmentRates(s)).length
              return (
                <div>
                  <Label className="text-sm font-medium">
                    Services & Rates
                    {activeCount > 0 && <span className="prc-active-count">{activeCount}</span>}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Enter rates for each service. Empty services won't be associated.
                  </p>
                  <div className="prc-svc-tabs">
                    {LABOR_SERVICE_TABS.map((svc) => (
                      <button key={svc} type="button"
                        className={`prc-svc-tab${activeEquipmentTab === svc ? ' active' : ''}${hasEquipmentRates(svc) ? ' has-rates' : ''}`}
                        onClick={() => setActiveEquipmentTab(svc)}
                      >
                        {svc}
                        {hasEquipmentRates(svc) && <span className="prc-svc-tab-dot" />}
                      </button>
                    ))}
                  </div>
                  <div className="prc-svc-rate-fields">
                    <div>
                      <Label htmlFor={`eq-rate-${activeEquipmentTab}`} className="text-xs font-medium text-muted-foreground">
                        Rate / Hr
                      </Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                          id={`eq-rate-${activeEquipmentTab}`}
                          type="number"
                          placeholder="0.00"
                          value={equipmentRates[activeEquipmentTab]?.rate || ''}
                          onChange={(e) => updateEquipmentRate(activeEquipmentTab, parseFloat(e.target.value) || 0)}
                          className="pl-7"
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* ═══ Misc: Service tabs with rate field ═══ */}
            {(category === 'miscellaneous' || category === 'misc') && (() => {
              const activeCount = LABOR_SERVICE_TABS.filter(s => hasMiscRates(s)).length
              return (
                <div>
                  <Label className="text-sm font-medium">
                    Services & Rates
                    {activeCount > 0 && <span className="prc-active-count">{activeCount}</span>}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Enter rates for each service. Empty services won't be associated.
                  </p>
                  <div className="prc-svc-tabs">
                    {LABOR_SERVICE_TABS.map((svc) => (
                      <button key={svc} type="button"
                        className={`prc-svc-tab${activeMiscTab === svc ? ' active' : ''}${hasMiscRates(svc) ? ' has-rates' : ''}`}
                        onClick={() => setActiveMiscTab(svc)}
                      >
                        {svc}
                        {hasMiscRates(svc) && <span className="prc-svc-tab-dot" />}
                      </button>
                    ))}
                  </div>
                  <div className="prc-svc-rate-fields">
                    <div>
                      <Label htmlFor={`misc-rate-${activeMiscTab}`} className="text-xs font-medium text-muted-foreground">
                        Cost Per Unit
                      </Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                          id={`misc-rate-${activeMiscTab}`}
                          type="number"
                          placeholder="0.00"
                          value={miscRates[activeMiscTab]?.rate || ''}
                          onChange={(e) => updateMiscRate(activeMiscTab, parseFloat(e.target.value) || 0)}
                          className="pl-7"
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* ═══ Materials: Service tabs with rate field ═══ */}
            {category === 'materials' && (() => {
              const activeCount = LABOR_SERVICE_TABS.filter(s => hasMaterialRates(s)).length
              return (
                <div>
                  <Label className="text-sm font-medium">
                    Services & Rates
                    {activeCount > 0 && <span className="prc-active-count">{activeCount}</span>}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Enter cost per unit for each service. Empty services won't be associated.
                  </p>
                  <div className="prc-svc-tabs">
                    {LABOR_SERVICE_TABS.map((svc) => (
                      <button key={svc} type="button"
                        className={`prc-svc-tab${activeMaterialTab === svc ? ' active' : ''}${hasMaterialRates(svc) ? ' has-rates' : ''}`}
                        onClick={() => setActiveMaterialTab(svc)}
                      >
                        {svc}
                        {hasMaterialRates(svc) && <span className="prc-svc-tab-dot" />}
                      </button>
                    ))}
                  </div>
                  <div className="prc-svc-rate-fields">
                    <div>
                      <Label htmlFor={`mat-rate-${activeMaterialTab}`} className="text-xs font-medium text-muted-foreground">
                        Cost Per Unit
                      </Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                          id={`mat-rate-${activeMaterialTab}`}
                          type="number"
                          placeholder="0.00"
                          value={materialRates[activeMaterialTab]?.rate || ''}
                          onChange={(e) => updateMaterialRate(activeMaterialTab, parseFloat(e.target.value) || 0)}
                          className="pl-7"
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* Fuel Charge — equipment only (read-only, set at company level) */}
            {category === 'equipment' && (
              <div>
                <p style={{ fontSize: '13px', color: 'var(--s500)', marginTop: '4px' }}>
                  Fuel Charge: {Math.round(defaultFuelChargePercent)}% — <span style={{ fontStyle: 'italic' }}>Set by the company</span>
                </p>
              </div>
            )}

            {/* Unit Type — miscellaneous */}
            {category === 'miscellaneous' && (
              <div>
                <Label htmlFor="unit" className="text-sm font-medium">
                  Unit Type
                </Label>
                <select
                  id="unit"
                  value={formData.unit}
                  onChange={(e) =>
                    setFormData({ ...formData, unit: e.target.value })
                  }
                  className="w-full mt-2 px-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
                >
                  {UNIT_TYPE_OPTIONS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Striping: Unit type */}
            {category === 'striping' && (
              <div>
                <Label htmlFor="stripingUnit" className="text-sm font-medium">
                  Unit Type
                </Label>
                <select
                  id="stripingUnit"
                  value={formData.unit}
                  onChange={(e) =>
                    setFormData({ ...formData, unit: e.target.value })
                  }
                  className="w-full mt-2 px-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
                >
                  <option value="each">Each</option>
                  <option value="lf">Linear Foot</option>
                  <option value="sf">Square Foot</option>
                </select>
              </div>
            )}

            {/* Striping: Restripe Cost */}
            {category === 'striping' && (
              <div>
                <Label htmlFor="restripeCost" className="text-sm font-medium">
                  Restripe Cost
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="restripeCost"
                    type="number"
                    placeholder="0.00"
                    value={formData.restripeCost || ''}
                    onChange={(e) => applyRestripeCost(parseFloat(e.target.value) || 0)}
                    className="pl-7"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
              </div>
            )}

            {/* Striping: Category dropdown */}
            {category === 'striping' && (
              <div>
                <Label htmlFor="stripingCategory" className="text-sm font-medium">
                  Category
                </Label>
                <select
                  id="stripingCategory"
                  value={formData.stripingCategory}
                  onChange={(e) =>
                    setFormData({ ...formData, stripingCategory: e.target.value })
                  }
                  className="w-full mt-2 px-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
                >
                  {STRIPING_CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Striping: New Layout Enabled checkbox + conditional cost */}
            {category === 'striping' && (
              <div>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="checkbox"
                    id="newLayoutEnabled"
                    checked={formData.newLayoutEnabled}
                    onChange={(e) => toggleNewLayout(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="newLayoutEnabled" className="text-sm font-medium cursor-pointer">
                    New Layout Enabled
                  </Label>
                </div>
                {formData.newLayoutEnabled && (
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="newLayoutAdditionalCost"
                      type="number"
                      placeholder="0.00"
                      value={formData.newLayoutAdditionalCost || ''}
                      onChange={(e) => setNewLayoutCost(parseFloat(e.target.value) || 0)}
                      className="pl-7"
                      step="0.01"
                      min="0"
                    />
                  </div>
                )}
                {formData.newLayoutEnabled && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {newLayoutAuto
                      ? `Auto-set to ${Math.round(NEW_LAYOUT_PCT * 100)}% of the restripe cost — edit to override.`
                      : 'Custom amount.'}
                  </p>
                )}
              </div>
            )}

            {/* Striping: Double Coat Enabled checkbox + conditional cost */}
            {category === 'striping' && (
              <div>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="checkbox"
                    id="doubleCoatEnabled"
                    checked={formData.doubleCoatEnabled}
                    onChange={(e) => toggleDoubleCoat(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="doubleCoatEnabled" className="text-sm font-medium cursor-pointer">
                    Double Coat Enabled
                  </Label>
                </div>
                {formData.doubleCoatEnabled && (
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="doubleCoatAdditionalCost"
                      type="number"
                      placeholder="0.00"
                      value={formData.doubleCoatAdditionalCost || ''}
                      onChange={(e) => setDoubleCoatCost(parseFloat(e.target.value) || 0)}
                      className="pl-7"
                      step="0.01"
                      min="0"
                    />
                  </div>
                )}
                {formData.doubleCoatEnabled && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {doubleCoatAuto
                      ? `Auto-set to ${Math.round(DOUBLE_COAT_PCT * 100)}% of the restripe cost — edit to override.`
                      : 'Custom amount.'}
                  </p>
                )}
              </div>
            )}

            {/* Striping: price preview — shows how much each selected option adds and
                the resulting item price, before the item is added. */}
            {category === 'striping' && (formData.newLayoutEnabled || formData.doubleCoatEnabled) && (() => {
              const base = formData.restripeCost || 0
              const nl = formData.newLayoutAdditionalCost || 0
              const dc = formData.doubleCoatAdditionalCost || 0
              const money = (n: number) => `$${n.toFixed(2)}`
              return (
                <div className="rounded-md border border-input bg-muted/40 p-3 text-sm space-y-1">
                  <div className="font-medium mb-1">Price preview</div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Base (restripe)</span>
                    <span>{money(base)}</span>
                  </div>
                  {formData.newLayoutEnabled && (
                    <div className="flex justify-between">
                      <span>+ New Layout {newLayoutAuto ? `(${Math.round(NEW_LAYOUT_PCT * 100)}%)` : '(custom)'}</span>
                      <span>+{money(nl)} → {money(base + nl)}</span>
                    </div>
                  )}
                  {formData.doubleCoatEnabled && (
                    <div className="flex justify-between">
                      <span>+ Double Coat {doubleCoatAuto ? `(${Math.round(DOUBLE_COAT_PCT * 100)}%)` : '(custom)'}</span>
                      <span>+{money(dc)} → {money(base + dc)}</span>
                    </div>
                  )}
                  {formData.newLayoutEnabled && formData.doubleCoatEnabled && (
                    <div className="flex justify-between font-medium border-t border-input mt-1 pt-1">
                      <span>+ Both</span>
                      <span>+{money(nl + dc)} → {money(base + nl + dc)}</span>
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Rate / Per Hour — not shown for striping, labor, equipment, misc, or materials (those use tabs) */}
            {category !== 'striping' && category !== 'labor' && category !== 'equipment' && category !== 'miscellaneous' && category !== 'misc' && category !== 'materials' && (
              <div>
                <Label htmlFor="rate" className="text-sm font-medium">
                  {category === 'equipment' ? 'Per Hour' : 'Cost Per Unit'}
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="rate"
                    type="number"
                    placeholder="0.00"
                    value={formData.rate || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        rate: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="pl-7"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
              </div>
            )}

            {/* Unit Type — materials only */}
            {category === 'materials' && (
              <div>
                <Label htmlFor="unitMaterials" className="text-sm font-medium">
                  Unit Type
                </Label>
                <select
                  id="unitMaterials"
                  value={formData.unit}
                  onChange={(e) =>
                    setFormData({ ...formData, unit: e.target.value })
                  }
                  className="w-full mt-2 px-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
                >
                  {MATERIALS_UNIT_OPTIONS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: 'var(--red)', fontSize: 13 }}>
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={!formData.name.trim()}
              >
                {editItem ? 'Save Changes' : (category === 'labor' ? 'Add Laborer' : 'Add Item')}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  )
}
